import React, { useState } from 'react';
import { Headphones, Send, Loader2 } from 'lucide-react';
import { Card, Input, Button } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useSubmitTicket } from '../hooks/useSubmitTicket';
import { toast } from 'sonner';

export function SupportTicketCard() {
  const { mutateAsync: submitTicket, isPending } = useSubmitTicket();
  const [formData, setFormData] = useState({
    title: '',
    category: 'system',
    priority: 'normal',
    description: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Vui lòng nhập đầy đủ tiêu đề và nội dung yêu cầu.');
      return;
    }

    try {
      const res = await submitTicket(formData);
      toast.success(`Đã gửi yêu cầu hỗ trợ thành công! Mã: ${res.ticketId}`);
      setFormData({ title: '', category: 'system', priority: 'normal', description: '' });
    } catch (error) {
      toast.error('Có lỗi xảy ra khi gửi yêu cầu. Vui lòng thử lại sau.');
    }
  };

  return (
    <Card className="p-6 border-slate-100 shadow-sm bg-white sticky top-[80px]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <Headphones size={20} />
        </div>
        <div>
          <h3 className="sm:text-sm text-xs font-semibold text-slate-900">Liên hệ Hỗ trợ</h3>
          <p className="text-[10px] text-slate-500">Gửi Ticket trực tiếp cho Admin</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-700">Tiêu đề yêu cầu <span className="text-rose-500">*</span></label>
          <Input 
            value={formData.title}
            onChange={e => setFormData({...formData, title: e.target.value})}
            placeholder="Tóm tắt vấn đề..."
            className="sm:text-sm text-xs border-slate-200 rounded-md h-9"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-700">Chuyên mục</label>
            <select 
              value={formData.category}
              onChange={e => setFormData({...formData, category: e.target.value})}
              className="w-full sm:text-sm text-xs border-slate-200 rounded-md h-9 px-3 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-slate-700 bg-white"
            >
              <option value="system">Lỗi hệ thống</option>
              <option value="permission">Phân quyền</option>
              <option value="feature">Tính năng mới</option>
              <option value="other">Khác</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-700">Mức độ</label>
            <select 
              value={formData.priority}
              onChange={e => setFormData({...formData, priority: e.target.value})}
              className="w-full sm:text-sm text-xs border-slate-200 rounded-md h-9 px-3 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-slate-700 bg-white"
            >
              <option value="low">Thấp</option>
              <option value="normal">Bình thường</option>
              <option value="high">Cao</option>
              <option value="urgent">Khẩn cấp</option>
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-700">Chi tiết vấn đề <span className="text-rose-500">*</span></label>
          <textarea 
            value={formData.description}
            onChange={e => setFormData({...formData, description: e.target.value})}
            placeholder="Mô tả chi tiết vấn đề bạn đang gặp phải..."
            className="w-full sm:text-sm text-xs border border-slate-200 rounded-md p-3 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-slate-700 resize-none h-28"
          />
        </div>

        <div className="pt-2">
          <AuthButton 
            permission="system.settings.view" 
            behavior="disable" 
            permissionReason="Chỉ dành cho các cấp Quản lý để gửi Ticket hỗ trợ ưu tiên"
            type="submit"
            disabled={isPending}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-10 rounded-md sm:text-sm text-xs flex justify-center items-center shadow-md shadow-emerald-700/10 transition-colors"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {isPending ? 'Đang gửi...' : 'Gửi yêu cầu hỗ trợ'}
          </AuthButton>
        </div>
      </form>
      
      <div className="mt-6 pt-4 border-t border-slate-100">
        <p className="text-[10px] text-slate-400 text-center">
          Hotline hỗ trợ kỹ thuật: <br/>
          <strong className="text-emerald-700">0344 654 435</strong> (Giờ hành chính)
        </p>
      </div>
    </Card>
  );
}
